import type { BundleItem } from '../../../stores/rewards/types';
import type { ClaimableReward } from '../../../stores/ui/types';

// Mirrors the `RewardItem` shape documented in POSTMESSAGE_CONTRACT.md. Fields are
// sparse — only the ones relevant to `currencyCode` are populated. `frbPackage` is
// not in the public contract but the iframe sends it on FRB items so the host knows
// whether it's a bonus-code free-spin or a minigame-round grant.
export type MinigameRewardBundleItem = {
  bundleType?: 'freeSpins' | 'minigameRound';
  bundlePayload?: {
    gameId?: number;
    rounds?: number;
    gameType?: string;
    provider?: string;
    gameImageUrl?: string;
    bet?: number;
    coinType?: 'GC' | 'SC';
  };
};

export type MinigameRewardItem = {
  currencyCode: 'SC' | 'GC' | 'FRB' | 'METAGAME_TOKEN' | 'SNAPPY_GIFT' | 'EXTRA_PERCENT';
  amount?: number;
  offerRewardImage?: string;
  offerExtraPercent?: number;
  bonusCode?: string;
  bonusCodeImageUrl?: string;
  metagameTokenAmount?: number;
  metagameTokenMode?: string;
  metagameTokenName?: string;
  metagameTokenImageUrl?: string;
  metagameTokenType?: number;
  snappyGiftImageUrl?: string;
  snappyGiftCampaignId?: string;
  frbPackage?: { bundleItems?: MinigameRewardBundleItem[] };
};

// FRB envelope carries either a casino bonus-code free-spin OR a minigame-round grant —
// the bundleType inside frbPackage.bundleItems is the discriminator.
export const buildFrbBundleItem = (item: MinigameRewardItem, index: number): BundleItem => {
  const bundleItems = item.frbPackage?.bundleItems;
  const minigame = bundleItems?.find((b) => b?.bundleType === 'minigameRound')?.bundlePayload;
  const freeSpins = bundleItems?.find((b) => b?.bundleType === 'freeSpins')?.bundlePayload;
  const isMinigame = !!minigame;
  const rounds = Number(minigame?.rounds) || 0;
  return {
    id: `frb-${isMinigame ? 'minigame' : 'bonus'}-${index}`,
    bundleType: isMinigame ? 'minigameRound' : 'freeSpins',
    bundlePayload: isMinigame
      ? {
          type: 'freeSpins', // server-shared shape — see MinigameRoundPayload note in stores/rewards/types.ts
          provider: minigame?.provider || 'Minigames',
          gameId: Number(minigame?.gameId),
          gameType: minigame?.gameType,
          gameImageUrl: minigame?.gameImageUrl,
          imageUrl: minigame?.gameImageUrl,
          rounds,
        }
      : {
          type: 'freeSpins',
          // The authored bundle carries the real casino provider; hardcoding Barouletta here routed
          // every won free-spin to FRBWidget's image-only branch instead of the pink spins widget.
          provider: freeSpins?.provider || 'Barouletta Gaming',
          bonusCode: item.bonusCode,
          bonusCodeImageUrl: item.bonusCodeImageUrl,
          gameImageUrl: freeSpins?.gameImageUrl || item.bonusCodeImageUrl,
          displayCount: Number(item.amount) || 0,
          ...(freeSpins && {
            rounds: Number(freeSpins.rounds) || 0,
            bet: freeSpins.bet,
            coinType: freeSpins.coinType,
          }),
        },
  } as unknown as BundleItem;
};

// Single-pass conversion of the iframe's `playAnimation*` rewards payload to the shape the
// coins-animation pipeline expects. Sums SC/GC and fans each non-coin entry into one
// `bundleItems` array.
export const buildClaimableRewards = (rewards: MinigameRewardItem[] | undefined): ClaimableReward[] => {
  let scCoin = 0;
  let gcCoin = 0;
  const bundleItems: BundleItem[] = [];

  (rewards ?? []).forEach((item, index) => {
    if (!item) return;
    switch (item.currencyCode) {
      case 'SC':
        scCoin += Number(item.amount || 0);
        break;
      case 'GC':
        gcCoin += Number(item.amount || 0);
        break;
      case 'METAGAME_TOKEN':
        bundleItems.push({
          // ClaimRewardsAnimation de-dupes by id — without a unique one every token entry
          // collapses into the first and the player is shown a partial amount.
          id: `metagame-token-${index}`,
          bundleType: 'metagameToken',
          bundlePayload: {
            // Both fields are optional; ClaimRewardsAnimation merges tokens with `+=`, so an
            // undefined here poisons the running total of every other token too.
            amount: Number(item.metagameTokenAmount ?? item.amount ?? 0),
            coinType: item.metagameTokenMode,
            currencyName: item.metagameTokenName,
            currencySymbol: item.metagameTokenImageUrl,
            metagameTypeId: item.metagameTokenType,
          },
        } as unknown as BundleItem);
        break;
      case 'FRB':
        bundleItems.push(buildFrbBundleItem(item, index));
        break;
      case 'SNAPPY_GIFT':
        bundleItems.push({
          id: `snappy-gift-${index}`,
          bundleType: 'snappyGift',
          bundlePayload: {
            type: 'snappyGift',
            popupImageUrl: item.snappyGiftImageUrl,
            widgetImageUrl: item.snappyGiftImageUrl,
            campaignId: item.snappyGiftCampaignId || '',
          },
        } as unknown as BundleItem);
        break;
      case 'EXTRA_PERCENT':
        bundleItems.push({
          id: `extra-offer-${index}`,
          bundleType: 'extraOffer',
          bundlePayload: {
            benefitKey: '',
            variation: '',
            rewardImageUrl: item.offerRewardImage,
            extraPercentage: item.offerExtraPercent,
          },
        } as unknown as BundleItem);
        break;
    }
  });

  return [{ scCoin, gcCoin, bundleItems }];
};
